import React, { FC, useState } from 'react';
import clsx from 'clsx';
import { useForm } from 'react-hook-form';

import { Bestandsvalidatiefouttype, TamResultaat } from '@/components/dialogs/beheren/tam/TamResultaat';
import { ConfirmationDialog } from '@/components/dialogs/generic/confirmation';
import MwdDialog from '@/components/UI/Dialog/Dialog';
import { InputField } from '@/components/UI/Fields/ReactFormFields/CreateFieldComponent';
import { Label } from '@/components/UI/Label/Label';
import { LoaderWithOverlay } from '@/components/UI/Loader/LoaderWithOverlay';
import { useLoading } from '@/contextProviders/loadingProvider';
import { useTamUpload } from '@/queries/mutaties/useTamUpload';

import tamUploadStyles from './TamUploadDialog.module.css';
import fieldsStyles from '@/components/UI/Fields/Fields.module.css';

export type TamUploadDialogProps = {
  showDialog: boolean;
  handleOpenChange: (p: boolean) => void;
};

const TamUploadDialog: FC<TamUploadDialogProps> = ({ showDialog, handleOpenChange }) => {
  const { tamUpload } = useTamUpload();
  const { loading, setLoading } = useLoading();
  const [files, setFiles] = useState<File[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isDryRun, setIsDryRun] = useState(false);
  const [tamResult, setTamResult] = useState<TamResultaat | null>(null);

  const { handleSubmit, reset, control, clearErrors } = useForm({
    mode: 'onSubmit',
  });

  const resetDialog = () => {
    handleOpenChange(false);
    reset();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
      clearErrors();
      setTamResult(null);
    }
  };

  const handleSubmitType = (type: string) => {
    setIsDryRun(type !== 'submit');
  };

  const onSubmit = async () => {
    setTamResult(null);
    setShowConfirmDialog(true);
  };

  const performTam = async () => {
    setShowConfirmDialog(false);
    const formData = new FormData();
    [...files].forEach((file) => formData.append('bestanden', file));

    const resultTam = await tamUpload(formData, isDryRun);
    setTamResult(resultTam);
  };

  const formatBewegingen = (bewegingen: Array<number>): string => {
    const gesorteerdeBewegingen = bewegingen.sort((a, b) => a - b);
    return gesorteerdeBewegingen.length > 0 ? gesorteerdeBewegingen.join(', ') : 'Geen';
  };

  const formatBestandsvalidatiefouten = (bestandsvalidatiefouten: Array<Bestandsvalidatiefouttype>): string => {
    const berichten: { [key in Bestandsvalidatiefouttype]: string } = {
      [Bestandsvalidatiefouttype.LEEG_BESTAND]: 'het bestand is leeg',
      [Bestandsvalidatiefouttype.TE_LANGE_REGEL]: 'het bestand bevat een te lange regel',
      [Bestandsvalidatiefouttype.FORMAT_FOUT]: 'het bestand bevat een ongeldige waarde',
    };

    return bestandsvalidatiefouten.map((fout) => berichten[fout]).join(', ');
  };

  return (
    <>
      {loading && (
        <LoaderWithOverlay isLoading={loading} handleOpenChange={setLoading} overlayOpacity='nonTransparant' />
      )}
      <MwdDialog
        title='Start TAM verwerking'
        className={tamUploadStyles.tamUploadDialogStyle}
        showDialog={showDialog}
        handleOpenChange={handleOpenChange}
        scrollEnabled={true}
        hasCloseInHeader={true}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div>
            <InputField
              className={clsx(fieldsStyles.inputField, tamUploadStyles.tamUploadInputStyle)}
              autoComplete='off'
              control={control}
              placeholder='Upload bestanden'
              data-testid='test-bestanden-uploaden-input'
              name='bestanden'
              label='Bestanden uploaden (selecteer alle bestanden in één keer)'
              type='file'
              multiple
              accept='.txt, .csv'
              onChange={handleFileChange}
              rules={{
                validate: {
                  minLength: () => {
                    return files.length > 0 ? true : 'Upload minimaal 1 bestand';
                  },
                },
              }}
              showOptional={false}
            />
            {files &&
              [...files].map((file) => (
                <React.Fragment key={`file-${file.name}`}>
                  <div
                    style={{
                      height: '2px',
                      marginBottom: '8px',
                      marginTop: '8px',
                      width: 'full',
                      background: 'borderSubtle',
                    }}
                  />
                  <div>
                    <ul>
                      <li>
                        <Label>Bestand: </Label> {file.name}
                      </li>
                      <li>
                        <Label>Grootte: </Label> {(file.size / 1024).toFixed(2)} kB
                      </li>
                    </ul>
                  </div>
                </React.Fragment>
              ))}
          </div>
          <div
            style={{
              height: '2px',
              marginBottom: '14px',
              marginTop: '14px',
              width: 'full',
              background: 'borderSubtle',
            }}
          />
          {tamResult && (
            <div style={{ display: 'flex', gap: 3, flexDirection: 'column' }}>
              <h2>Resultaat: {isDryRun ? 'testdraaien' : 'volledige verwerking'}</h2>
              {tamResult.type === 'TamSuccesResultaatDto' && (
                <ul>
                  <li>
                    <Label>Nog in te leggen treinen: </Label>
                    {formatBewegingen(tamResult.aanTeMakenBewegingen)}
                  </li>
                  <li>
                    <Label>Te wijzigen treinen: </Label>
                    {formatBewegingen(tamResult.teWijzigenBewegingen)}
                  </li>
                  <li>
                    <Label>Nog op te heffen treinen: </Label>
                    {formatBewegingen(tamResult.opTeHeffenBewegingen)}
                  </li>
                  <li>
                    <Label>Manco werklijnen op LUD-dag:</Label> {tamResult.tamDienstenZonderEenheid}
                  </li>
                  <li>
                    <Label>Manco werklijnen ná LUD-dag:</Label> {tamResult.tamDienstenZonderUitloop}
                  </li>
                </ul>
              )}
              {/* Upload het bestand src/mocks/mockData/tam_response_validatiefout.txt om tijdens development deze flow te triggeren. */}
              {tamResult.type === 'TamValidatieFoutResultaatDto' && (
                <>
                  <p>
                    De verwerking is mislukt. Controleer de volgende bewegingen in het plan en laat deze door TAM
                    aanpassen. Probeer het daarna opnieuw.
                  </p>
                  <ul>
                    {tamResult.bewegingenMetValidatieFouten && tamResult.bewegingenMetValidatieFouten.length > 0 && (
                      <li>
                        <Label>Bij de onderstaande bewegingen is een validatiefout opgetreden: </Label>
                        {formatBewegingen(tamResult.bewegingenMetValidatieFouten)}
                      </li>
                    )}
                    {tamResult.bestandsvalidatiefouten && tamResult.bestandsvalidatiefouten.length > 0 && (
                      <li>
                        <Label>Bij het inlezen van de bestanden zijn de volgende fouten opgetreden: </Label>
                        {formatBestandsvalidatiefouten(tamResult.bestandsvalidatiefouten)}
                      </li>
                    )}
                  </ul>
                </>
              )}

              <div
                style={{
                  height: '2px',
                  marginBottom: '14px',
                  marginTop: '14px',
                  width: 'full',
                  background: 'borderSubtle',
                }}
              />
            </div>
          )}
          <MwdDialog.Footer
            submitCaption='Start TAM verwerking'
            confirmButtonType='destructive'
            secondarySubmitCaption='Testdraaien'
            cancelCallback={resetDialog}
            justifyContentStyle='space-between'
            handleSubmitType={handleSubmitType}
          />
          {showConfirmDialog && (
            <ConfirmationDialog
              handleOpenChange={setShowConfirmDialog}
              showDialog={showConfirmDialog}
              onCancel={() => setShowConfirmDialog(false)}
              onConfirm={performTam}
              confirmButtonType={isDryRun ? 'primary' : 'destructive'}
              cancelText='Annuleren'
              confirmText={isDryRun ? 'Testdraaien' : 'Starten'}
              title='Weet je het zeker?'
            >
              {isDryRun ? (
                <>
                  <span>
                    Je staat op het punt een testdraai van het TAM-plan uit te voeren. Dit duurt ongeveer{' '}
                    <span style={{ fontWeight: 700 }}>40 seconden</span>. Dit heeft geen invloed op gebruikers of de
                    werklijnen.
                  </span>
                  <p style={{ fontWeight: 700, color: 'var(--tractie-color-content-emphasis)' }}>Wil je doorgaan?</p>
                </>
              ) : (
                <>
                  <span>
                    Je staat op het punt het TAM-plan te implementeren. Dit houdt in dat alle werklijnen worden
                    vergrendeld en vervolgens worden bijgesteld volgens het voorgestelde plan.
                  </span>
                  <span>
                    Na ongeveer <span style={{ fontWeight: 700 }}>1 minuut en 30 seconden</span> ontvang je het
                    resultaat van de verwerking. Het kan tot <span style={{ fontWeight: 700 }}>7 minuten</span> na de
                    start duren voordat alle werklijnen bijgewerkt zichtbaar en vrij te muteren zijn.
                  </span>
                  <p style={{ fontWeight: 700, color: 'var(--tractie-color-content-emphasis)' }}>
                    Deze actie kan niet worden teruggedraaid. Wil je doorgaan?
                  </p>
                </>
              )}
            </ConfirmationDialog>
          )}
        </form>
      </MwdDialog>
    </>
  );
};
export default TamUploadDialog;
